<template>
  <main v-if="gameserverIndex != null" data-testid="gameserver-index-wrapper">
    <common-breadcrumb
      class="mx-auto max-w-screen-xl px-4 py-8 xl:pt-16"
      :crumbs="breadCrumbs"
    />

    <strapi-countdown
      v-if="gameserverIndex.countdown?.endDate"
      class="mx-auto max-w-screen-xl px-4"
      :component="gameserverIndex.countdown"
    />

    <gameserver-index-top-games
      class="mx-auto max-w-screen-xl px-4 z-1 py-8"
      :products="topProducts"
    />

    <gameserver-index-head
      v-once
      class="mx-auto max-w-screen-xl px-4 py-12 lg:py-20 text-center"
      :headline="gameserverIndex.headline"
      :description="gameserverIndex.description"
    />

    <gameserver-index-game-list
      class="mx-auto max-w-screen-xl px-4 py-12 lg:py-16"
    />

    <div
      class="bg-[url('/sc/images/svgs/landingpage_bg.svg')] bg-repeat bg-fixed bg-depth-0 py-16"
    >
      <strapi-product-benefits
        :component="gameserverIndex.benefits"
        large-header
      />
    </div>

    <strapi-product-faq
      data-cy="product-faq"
      class="mb-12"
      :component="gameserverIndex.faq"
    />
  </main>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/nuxt';
import {
  GetGameserverIndex,
  type GetGameserverIndexQuery,
  type GetGameserverIndexQueryVariables,
  type StrapiGameserverIndex,
  type StrapiProduct,
  type TrustPilotScore,
} from '~/apollo/types/types';
import { getSmallestImageURL } from '~/helpers/strapiHelper';
import { useGoogleFaqPage } from '~/helpers/googleFaqPage';
import { useGoogleBreadcrumb } from '~/helpers/googleBreadcrumb';
import { serializeJsonLd } from '~/helpers/jsonLd';

const { t, locale } = useI18n();
const currencyStore = useCurrencyStore();
const config = useRuntimeConfig();

const currentCurrency = computed(() => currencyStore.currency);
const { refresh, data, status } = await useAsyncData<{
  products: Array<StrapiProduct>;
  gameserver: StrapiGameserverIndex;
  trustpilotScore: TrustPilotScore;
}>(
  `gp-gameserver-index-${locale.value}-${currentCurrency.value}`,
  async () => {
    const ret = {
      products: [] as Array<StrapiProduct>,
      gameserver: null as StrapiGameserverIndex,
      trustpilotScore: null as TrustPilotScore,
    };

    try {
      const { data: response, error: responseError } = await useLegacyApollo<
        GetGameserverIndexQuery,
        GetGameserverIndexQueryVariables
      >(GetGameserverIndex, {
        currency: currentCurrency.value,
        locale: locale.value,
      });

      // in rare case that the response is null
      if (response == null) {
        if (responseError != null) {
          throw responseError;
        }

        throw new Error('No response from server');
      }

      if (response.gameserver == null) {
        throw new Error(`No gameserver index found for locale ${locale.value}`);
      }

      ret.products = response.products as Array<StrapiProduct>;
      ret.gameserver = response.gameserver as StrapiGameserverIndex;
      ret.trustpilotScore = response.reviewScore as TrustPilotScore;
    } catch (e: unknown) {
      captureException(e);

      throw e;
    }

    return ret;
  },
  {
    default: () => ({
      products: [] as Array<StrapiProduct>,
      gameserver: null,
      trustpilotScore: null,
    }),
  },
);

const { redirectToDefaultLocale } = useRedirectToLocale();
if (
  data.value.gameserver == null &&
  locale.value !== 'en' &&
  status.value === 'success'
) {
  await redirectToDefaultLocale();
}

const gameserverIndex = computed(() => data.value.gameserver);
const topProducts = computed(() => data.value.products);

const localizedSlugs = computed(() => gameserverIndex.value?.localizedSlugs);
useI18nLocalizedSlugs(localizedSlugs);

const route = useRoute();

const breadCrumbs = computed(() => [
  { title: t('general/menu/item/game_servers'), link: '' },
]);

const googleFaqPage = computed(() =>
  useGoogleFaqPage(gameserverIndex.value?.faq?.frequentlyAskedQuestions),
);

const googleBreadcrumb = computed(() =>
  useGoogleBreadcrumb(
    config.public.websiteUrl,
    locale.value,
    t('app/home'),
    breadCrumbs.value,
    route.path,
  ),
);

useHead({
  title: () => data.value.gameserver?.metaTitle,
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: `/sc/images/svgs/landingpage_bg.svg`,
    },
  ],
  script: () => {
    const scripts = [];
    if (googleFaqPage.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(googleFaqPage.value),
      });
    }
    if (googleBreadcrumb.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(googleBreadcrumb.value),
      });
    }
    return scripts;
  },
});

const getSocialMediaImageUrl = () => {
  const trustPilotImage = data.value.gameserver?.trustpilot?.backgroundImage;
  if (trustPilotImage == null) {
    return undefined;
  }

  return {
    url: getSmallestImageURL(trustPilotImage),
  };
};
useSeoMeta({
  title: () => data.value.gameserver?.metaTitle,
  description: () => data.value.gameserver?.metaDescription,
  ogTitle: () => data.value.gameserver?.metaTitle,
  ogDescription: () => data.value.gameserver?.metaDescription,
  ogImage: getSocialMediaImageUrl,
  ogUrl: `${config.public.websiteUrl}${route.fullPath}`,
  ogImageAlt: () => data.value.gameserver?.metaTitle,
  twitterTitle: () => data.value.gameserver?.metaTitle,
  twitterDescription: () => data.value.gameserver?.metaDescription,
  twitterImage: getSocialMediaImageUrl,
  twitterImageAlt: () => data.value.gameserver?.metaTitle,
});

const { scrollToTop } = useWindowScroll();

// watch currency change -> re-fetch content
useUserCurrency(refresh, () => {
  scrollToTop();
  return refresh();
});

// watch for region changes -> re-fetch content
const authStore = useAuthStore();
const regionWatchHandle = watch(
  () => authStore.activeRegion,
  async () => {
    await refresh();
    scrollToTop();
  },
);

onUnmounted(() => {
  regionWatchHandle();
});
</script>
